import React from 'react';
import { api } from '../api.js';
import { Empty, Lbl, Loading, Modal, PH, Panel } from './components.jsx';
import { PeoplePicker } from './people-picker.jsx';
import { PEOPLE } from './data.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';

// booking-admin.jsx — 설비 관리(설비 관리자, 계획 문서 5.2 2-3a): 분류·이름·위치·정원·이용 요일·시간대·최대 길이·담당자·사용 여부.
// 권한·값 규칙은 서버가 판단한다(탭은 GET /facilities의 actions.manage일 때만 보이고, 관리 API는 400·403·409로 이유를 돌려준다).
// 승인 필요(2-3b): 켜면 그 설비의 새 예약은 담당자 승인 뒤 확정 — 재직 중인 담당자가 있어야 한다(서버가 400으로 거부).

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const WD = ['월', '화', '수', '목', '금', '토', '일'];
const EVERY_DAY = WD.map((_, i) => i + 1);   // ISO 1=월 … 7=일
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, boxSizing: 'border-box' };
const pad = n => String(n).padStart(2, '0');
/** 30분 단위 시각 'HH:MM' — 시작은 00:00~23:30, 끝은 00:30~24:00 */
const HALF_HOURS = Array.from({ length: 49 }, (_, i) => `${pad(Math.floor(i / 2))}:${i % 2 ? '30' : '00'}`);
const daysLabel = days => (days.length === 7 ? '매일' : days.map(d => WD[d - 1]).join('·'));
const durationText = min => {
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  return [d && `${d}일`, h && `${h}시간`, m && `${m}분`].filter(Boolean).join(' ');
};

/** 설비 만들기·고치기 — 바꾼 이용 시간·최대 길이·정원은 새 예약부터(이미 있는 예약은 다시 검사하지 않는다) */
function FacilityEditor({ facility, types, onClose, onSaved, onToast }) {
  const f = facility;
  const [name, setName] = uS(f?.name ?? '');
  const [type, setType] = uS(f?.type ?? '');
  const [location, setLocation] = uS(f?.location ?? '');
  const [description, setDescription] = uS(f?.description ?? '');
  const [capacity, setCapacity] = uS(f?.capacity ?? '');
  // 새 설비의 첫 값은 기존 설비를 채운 값과 같다(매일 08:00–19:00) — 실제 값은 관리자가 고른다
  const [days, setDays] = uS(f?.open_days ?? EVERY_DAY);
  const [openTime, setOpenTime] = uS(f?.open_time ?? '08:00');
  const [closeTime, setCloseTime] = uS(f?.close_time ?? '19:00');
  const [maxHours, setMaxHours] = uS(f?.max_minutes ? String(f.max_minutes / 60) : '');   // 빈칸 = 제한 없음
  const [manager, setManager] = uS(f?.manager_user_id ? [f.manager_user_id] : []);
  const [approvalRequired, setApprovalRequired] = uS(f?.approval_required ?? false);
  const [active, setActive] = uS(f?.active ?? true);
  const [busy, setBusy] = uS(false);
  const toggleDay = d => setDays(ds => (ds.includes(d) ? ds.filter(x => x !== d) : [...ds, d].sort((a, b) => a - b)));
  // 지금 담당자가 재직자가 아니면 고르는 목록(PEOPLE = 재직자)에 없다 — 그대로 둘 수도, 뺄 수도 있게 따로 보인다
  const staleManager = f?.manager_user_id && manager[0] === f.manager_user_id && !PEOPLE.some(p => p.id === f.manager_user_id);
  const save = () => {
    setBusy(true);
    const body = {
      name, type, location: location.trim() || null, description: description.trim() || null,
      capacity: capacity === '' ? null : Number(capacity), openDays: days, openTime, closeTime,
      maxMinutes: maxHours === '' ? null : Math.round(Number(maxHours) * 60), managerUserId: manager[0] ?? null, approvalRequired, active
    };
    (f ? api.put('/api/v1/facilities/' + f.id, body) : api.post('/api/v1/facilities', body))
      .then(() => { onToast(f ? '설비를 고쳤습니다 — 새 예약부터 쓰입니다' : '설비를 만들었습니다'); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={560} title={f ? `설비 고치기 — ${f.name}` : '새 설비'} icon="building" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || !name.trim() || !type.trim() || !days.length} onClick={save}>저장</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-facility-editor>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="이름" value={name} onChange={e => setName(e.target.value)} maxLength={50} style={{ flex: '2 1 200px' }} />
          <Field label="분류 (있는 분류를 고르거나 새로 입력)" value={type} onChange={e => setType(e.target.value)} list="facility-types" maxLength={30} style={{ flex: '1 1 160px' }} />
          <datalist id="facility-types">{types.map(t => <option key={t} value={t} />)}</datalist>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="위치 (선택)" value={location} onChange={e => setLocation(e.target.value)} maxLength={100} style={{ flex: '2 1 200px' }} />
          <Field label="정원 (없으면 비움)" type="number" min="1" value={capacity} onChange={e => setCapacity(e.target.value)} style={{ flex: '1 1 120px' }} />
        </div>
        <Field label="설명 (선택)" value={description} onChange={e => setDescription(e.target.value)} maxLength={300} />
        <div>
          <Lbl style={{ marginBottom: 6 }}>이용 요일</Lbl>
          <div style={{ display: 'flex', gap: 5 }} data-open-days>
            {WD.map((w, i) => <Checkbox key={w} size="sm" label={w} checked={days.includes(i + 1)} onChange={() => toggleDay(i + 1)} />)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>이용 시작</Lbl>
            <select style={box} value={openTime} onChange={e => setOpenTime(e.target.value)} aria-label="이용 시작">
              {HALF_HOURS.slice(0, -1).map(t => <option key={t} value={t}>{t}</option>)}
            </select></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>이용 끝</Lbl>
            <select style={box} value={closeTime} onChange={e => setCloseTime(e.target.value)} aria-label="이용 끝">
              {HALF_HOURS.slice(1).map(t => <option key={t} value={t}>{t}</option>)}
            </select></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>최대 길이(시간, 비우면 제한 없음)</Lbl>
            <input type="number" step="0.5" min="0.5" style={{ ...box, width: 120 }} value={maxHours} onChange={e => setMaxHours(e.target.value)} aria-label="최대 길이(시간)" /></label>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>00:00–24:00으로 두면 여러 날에 걸친 예약(차량 등)을 받을 수 있습니다.</div>
        {staleManager
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }} data-stale-manager>
              담당자: {f.manager_name} <Pill tone="neutral" dot={false}>재직 아님</Pill>
              <button className="link" onClick={() => setManager([])}>빼기</button>
            </div>
          : <PeoplePicker label="담당자 (승인 요청을 받고 예약을 취소할 수 있는 사람)" value={manager} onChange={setManager} max={1} includeMe />}
        <Checkbox label="승인 필요" description="새 예약은 담당자 승인 뒤 확정 — 재직 중인 담당자가 있어야 합니다. 끄면 이미 대기 중인 예약은 그대로 승인·반려합니다"
          checked={approvalRequired} onChange={setApprovalRequired} />
        <Checkbox label="사용" description="예약 화면에 보임 — 끝나지 않은 예약이 있으면 중지할 수 없습니다" checked={active} onChange={setActive} />
      </div>
    </Modal>
  );
}

function FacilityAdmin({ onToast, onChanged }) {
  const [list, setList] = uS(null);
  const [editing, setEditing] = uS(null);   // 고칠 설비 또는 'new'
  const reload = uCb(() => api.get('/api/v1/facilities/manage')
    .then(r => setList(r.facilities))
    .catch(e => { setList([]); onToast(e.message); }), [onToast]);
  uE(() => { void reload(); }, [reload]);
  const types = list ? [...new Set(list.map(f => f.type))] : [];
  return (
    <Panel scroll>
      <PH title="설비" sub={list ? `${list.length}개 · 바꾼 이용 시간은 새 예약부터` : ''}
        right={<Button variant="primary" size="sm" onClick={() => setEditing('new')}>새 설비</Button>} />
      {!list ? <Loading title="불러오는 중" /> : (
        <div data-facility-admin>
          <DataTable columns={[
            { key: 'name', header: '설비', strong: true, render: f => <button className="link" onClick={() => setEditing(f)} data-facility-row={f.id}>{f.name}</button> },
            { key: 'type', header: '분류', width: 80 },
            { key: 'location', header: '위치', render: f => f.location ?? '—' },
            { key: 'capacity', header: '정원', mono: true, align: 'right', width: 52, render: f => f.capacity ?? '—' },
            { key: 'hours', header: '이용', render: f => <span className="mono" style={{ fontSize: 11.5 }}>{daysLabel(f.open_days)} {f.open_time}–{f.close_time}</span> },
            { key: 'max', header: '최대', width: 72, render: f => (f.max_minutes ? durationText(f.max_minutes) : '—') },
            { key: 'manager', header: '담당자', render: f => (f.manager_name ? <span>{f.manager_name}{f.manager_active ? '' : <span style={{ color: 'var(--ink-3)' }}> (재직 아님)</span>}</span> : '—') },
            { key: 'approval', header: '승인', width: 52, render: f => (f.approval_required ? '필요' : '—') },
            { key: 'upcoming_count', header: '남은 예약', mono: true, align: 'right', width: 64 },
            { key: 'active', header: '상태', width: 64, render: f => (f.active ? <Pill tone="ok" dot>사용</Pill> : <Pill tone="neutral" dot={false}>중지</Pill>) },
          ]} rows={list} />
        </div>
      )}
      {editing && <FacilityEditor facility={editing === 'new' ? null : editing} types={types} onToast={onToast}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload(); onChanged(); }} />}
    </Panel>
  );
}

export { FacilityAdmin };
